import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useLanguage } from '../../contexts/LanguageContext'
import seoConfig from '../../config/seoPages.json'
import entityConfig from '../../config/entitySeo.json'

const { siteUrl: SITE_URL, siteName: SITE_NAME, pages, privatePrefixes } = seoConfig
const DEFAULT_IMAGE = `${SITE_URL}${seoConfig.defaultImage}`
const PAGE_BY_PATH = Object.fromEntries(pages.map((page) => [page.path, page]))
const entityId = (fragment) => `${SITE_URL}/#${fragment}`
const MARKET_REFS = entityConfig.markets.map((market) => ({ '@id': entityId(`market-${market.id}`) }))
const SERVICE_REFS = entityConfig.services.map((service) => ({ '@id': entityId(`service-${service.id}`) }))

const FAQ_ITEMS = [
    ['Where can I see my receipts and proof?', 'Open Transactions. That page is your proof trail for send money, bills, donations, tax, and other completed activity.'],
    ['How does diaspora pricing work?', 'Diaspora users usually enter the local amount first for services back home, then MojoPayr shows the home-currency debit, FX, and fee clearly before payment.'],
    ['How do local payments work?', 'Local users stay in their resident market and use the lighter local pricing path, designed to be simpler and more affordable for everyday use.'],
    ['Can I donate or contribute and still get a receipt?', 'Yes. Donations and contribution flows create receipt records that can be reopened later in Transactions.'],
]

const normalizePath = (pathname) => pathname.replace(/\/+$/, '') || '/'

const upsertMeta = (selector, attributes) => {
    let tag = document.head.querySelector(selector)
    if (!tag) {
        tag = document.createElement('meta')
        document.head.appendChild(tag)
    }
    Object.entries(attributes).forEach(([key, value]) => tag.setAttribute(key, value))
}

const upsertLink = (rel, href) => {
    let tag = document.head.querySelector(`link[rel="${rel}"]`)
    if (!tag) {
        tag = document.createElement('link')
        tag.rel = rel
        document.head.appendChild(tag)
    }
    tag.href = href
}

const setSchema = (payload) => {
    let tag = document.getElementById('mojopayr-seo-schema')
    if (!tag) {
        tag = document.createElement('script')
        tag.id = 'mojopayr-seo-schema'
        tag.type = 'application/ld+json'
        document.head.appendChild(tag)
    }
    tag.textContent = JSON.stringify(payload)
}

const breadcrumbSchema = (page, canonical) => ({
    '@type': 'BreadcrumbList',
    '@id': `${canonical}#breadcrumb`,
    itemListElement: page.breadcrumbs.map((name, index) => {
        const pathParts = page.path.split('/').filter(Boolean)
        const ancestor = index === 0 ? '/' : `/${pathParts.slice(0, index).join('/')}`
        return {
            '@type': 'ListItem',
            position: index + 1,
            name,
            item: index === page.breadcrumbs.length - 1 ? canonical : `${SITE_URL}${ancestor}`,
        }
    }),
})

const createSchema = (page, canonical) => {
    const organizationId = `${SITE_URL}/#organization`
    const websiteId = `${SITE_URL}/#website`
    const financialServiceId = `${SITE_URL}/#financial-service`
    const brandId = `${SITE_URL}/#brand`
    const relatedServiceRefs = (entityConfig.pageServices[page.path] || [])
        .map((id) => ({ '@id': entityId(`service-${id}`) }))
    const pageNode = {
        '@type': page.schemaType, '@id': `${canonical}#webpage`, url: canonical,
        name: page.title, description: page.description, isPartOf: { '@id': websiteId },
        keywords: page.keywords.join(', '), inLanguage: ['en', 'fr'],
        publisher: { '@id': organizationId }, breadcrumb: { '@id': `${canonical}#breadcrumb` },
        primaryImageOfPage: { '@type': 'ImageObject', url: DEFAULT_IMAGE },
        about: relatedServiceRefs.length ? relatedServiceRefs : { '@id': organizationId },
        ...(relatedServiceRefs.length ? { mainEntity: relatedServiceRefs } : {}),
        ...(page.schemaType === 'BlogPosting' ? {
            headline: page.title, datePublished: page.datePublished,
            author: { '@id': organizationId }, image: DEFAULT_IMAGE,
        } : {}),
        ...(page.schemaType === 'Service' ? { provider: { '@id': organizationId } } : {}),
        ...(page.path === '/faq' ? {
            mainEntity: FAQ_ITEMS.map(([question, answer]) => ({
                '@type': 'Question', name: question,
                acceptedAnswer: { '@type': 'Answer', text: answer },
            })),
        } : {}),
    }
    const graph = [
        {
            '@type': 'Organization', '@id': organizationId, name: SITE_NAME,
            alternateName: entityConfig.alternateNames, url: `${SITE_URL}/`,
            description: entityConfig.description, industry: entityConfig.industry,
            category: entityConfig.businessCategory, email: entityConfig.supportEmail,
            logo: { '@type': 'ImageObject', '@id': `${SITE_URL}/#logo`, url: `${SITE_URL}/logo.png`, caption: SITE_NAME },
            image: { '@id': `${SITE_URL}/#logo` }, brand: { '@id': brandId },
            areaServed: MARKET_REFS, knowsAbout: SERVICE_REFS,
            subjectOf: [{ '@id': `${SITE_URL}/about#webpage` }, { '@id': `${SITE_URL}/press#webpage` }],
            contactPoint: {
                '@type': 'ContactPoint', contactType: 'customer support',
                email: entityConfig.supportEmail, availableLanguage: entityConfig.languages,
                areaServed: MARKET_REFS,
            },
        },
        {
            '@type': 'Brand', '@id': brandId, name: SITE_NAME,
            alternateName: entityConfig.alternateNames,
            description: entityConfig.description, url: `${SITE_URL}/`,
            logo: { '@id': `${SITE_URL}/#logo` },
        },
        {
            '@type': 'WebSite', '@id': websiteId, url: `${SITE_URL}/`, name: SITE_NAME,
            alternateName: entityConfig.alternateNames, description: entityConfig.description,
            publisher: { '@id': organizationId }, about: { '@id': organizationId }, inLanguage: ['en', 'fr'],
            potentialAction: {
                '@type': 'SearchAction',
                target: { '@type': 'EntryPoint', urlTemplate: `${SITE_URL}/help-center?search={search_term_string}` },
                'query-input': 'required name=search_term_string',
            },
        },
        {
            '@type': 'FinancialService', '@id': financialServiceId, name: `${SITE_NAME} Digital Payment Platform`,
            alternateName: entityConfig.alternateNames, description: entityConfig.description,
            url: `${SITE_URL}/`, image: DEFAULT_IMAGE, parentOrganization: { '@id': organizationId },
            category: entityConfig.businessCategory, serviceType: entityConfig.services.map((service) => service.name),
            areaServed: MARKET_REFS,
            hasOfferCatalog: {
                '@type': 'OfferCatalog', name: 'MojoPayr financial technology services',
                itemListElement: SERVICE_REFS.map((service) => ({ '@type': 'Offer', itemOffered: service })),
            },
        },
        {
            '@type': 'MobileApplication', '@id': `${SITE_URL}/#mobile-application`, name: SITE_NAME,
            operatingSystem: ['Android', 'iOS'], applicationCategory: 'FinanceApplication',
            applicationSubCategory: entityConfig.businessCategory, description: entityConfig.description,
            url: `${SITE_URL}/`, publisher: { '@id': organizationId }, about: { '@id': financialServiceId },
            featureList: entityConfig.services.map((service) => service.name),
            countriesSupported: entityConfig.markets.map((market) => market.name),
        },
        {
            '@type': 'SoftwareApplication', '@id': `${SITE_URL}/#software-application`, name: SITE_NAME,
            operatingSystem: 'Web', applicationCategory: 'FinanceApplication',
            applicationSubCategory: entityConfig.businessCategory, description: entityConfig.description,
            url: `${SITE_URL}/`, publisher: { '@id': organizationId }, about: { '@id': financialServiceId },
            featureList: entityConfig.services.map((service) => service.name),
            countriesSupported: entityConfig.markets.map((market) => market.name),
        },
        ...entityConfig.markets.map((market) => ({
            '@type': 'Country', '@id': entityId(`market-${market.id}`),
            name: market.name, identifier: market.code,
        })),
        ...entityConfig.services.map((service) => ({
            '@type': 'Service', '@id': entityId(`service-${service.id}`),
            name: service.name, description: service.description,
            serviceType: service.name, provider: { '@id': organizationId }, areaServed: MARKET_REFS,
        })),
        pageNode,
        breadcrumbSchema(page, canonical),
    ]

    return { '@context': 'https://schema.org', '@graph': graph }
}

const ProductionSeo = () => {
    const location = useLocation()
    const { lang } = useLanguage()

    useEffect(() => {
        const path = normalizePath(location.pathname)
        const page = PAGE_BY_PATH[path]
        const privateRoute = privatePrefixes.some((prefix) => path === prefix || path.startsWith(`${prefix}/`))
        const indexable = Boolean(page) && !privateRoute
        const canonical = `${SITE_URL}${path === '/' ? '/' : path}`
        const title = page?.title || 'Page Not Found | MojoPayr'
        const description = page?.description || 'The requested MojoPayr page is not publicly available.'
        const robots = indexable
            ? 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1'
            : 'noindex,nofollow,noarchive,nosnippet'

        document.documentElement.lang = lang === 'fr' ? 'fr' : 'en'
        document.title = title
        upsertLink('canonical', canonical)
        upsertMeta('meta[name="description"]', { name: 'description', content: description })
        ;['robots', 'googlebot', 'bingbot'].forEach((name) => {
            upsertMeta(`meta[name="${name}"]`, { name, content: robots })
        })
        const og = {
            'og:site_name': SITE_NAME,
            'og:type': page?.schemaType === 'BlogPosting' ? 'article' : 'website',
            'og:locale': lang === 'fr' ? 'fr_FR' : 'en_US',
            'og:url': canonical,
            'og:title': title,
            'og:description': description,
            'og:image': DEFAULT_IMAGE,
            'og:image:alt': `${title} - MojoPayr`,
            'og:image:width': '1536',
            'og:image:height': '1024',
        }
        Object.entries(og).forEach(([property, content]) => {
            upsertMeta(`meta[property="${property}"]`, { property, content })
        })
        const twitter = {
            'twitter:card': 'summary_large_image', 'twitter:title': title,
            'twitter:description': description, 'twitter:image': DEFAULT_IMAGE,
            'twitter:image:alt': `${title} - MojoPayr`,
        }
        Object.entries(twitter).forEach(([name, content]) => {
            upsertMeta(`meta[name="${name}"]`, { name, content })
        })

        if (indexable) setSchema(createSchema(page, canonical))
        else document.getElementById('mojopayr-seo-schema')?.remove()

        const verification = {
            'google-site-verification': process.env.REACT_APP_GOOGLE_SITE_VERIFICATION,
            'msvalidate.01': process.env.REACT_APP_BING_SITE_VERIFICATION,
        }
        Object.entries(verification).forEach(([name, content]) => {
            if (content) upsertMeta(`meta[name="${name}"]`, { name, content })
        })
    }, [lang, location.pathname])

    return null
}

export default ProductionSeo
